<template>
  <div class="space-y-3 pb-6">
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <div>
        <h2 class="text-lg font-bold text-gray-800">Campuses</h2>
        <p class="text-xs text-gray-500 mt-0.5">Manage your institution's campus locations</p>
      </div>
      <Button
        size="small"
        :label="showCreateForm ? 'Close Form' : 'New Campus'"
        :icon="showCreateForm ? 'pi pi-times' : 'pi pi-plus'"
        :severity="showCreateForm ? 'secondary' : undefined"
        @click="showCreateForm = !showCreateForm"
      />
    </div>

    <transition name="slide-fade">
      <div v-if="showCreateForm" class="bg-white border border-gray-300 rounded-xl shadow-sm overflow-hidden">
        <div class="bg-gray-50 border-b border-gray-300 px-4 py-2.5">
          <h3 class="text-sm font-semibold text-gray-700">Add New Campus</h3>
        </div>
        <div class="p-3">
          <form @submit.prevent="createNewCampus" class="flex gap-2 items-end">
            <div class="flex-1 flex flex-col gap-0.5">
              <label class="text-xs font-medium text-gray-600">Campus Name *</label>
              <InputText
                v-model="campusName"
                placeholder="e.g. Main Campus, North Campus"
                class="w-full"
                :class="{ 'p-invalid': getErrorMessage('name') }"
              />
              <small v-if="getErrorMessage('name')" class="text-red-500 text-xs">{{ getErrorMessage('name') }}</small>
            </div>
            <Button type="submit" label="Create" icon="pi pi-plus" size="small" severity="success"
              :loading="creating" :disabled="!campusName.trim()" data-testid="btn-settings-campus-create" />
          </form>
        </div>
      </div>
    </transition>

    <div class="bg-white border border-gray-300 rounded-xl shadow-sm overflow-hidden">
      <div class="px-4 py-2.5 border-b border-gray-300">
        <div class="flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
          <h3 class="text-sm font-semibold text-gray-700">
            All Campuses
            <span v-if="campuses?.length" class="text-xs font-normal text-gray-400 ml-1">({{ campuses.length }})</span>
          </h3>
          <div class="w-full sm:w-64">
            <InputText v-model="searchQuery" placeholder="Search campuses..." class="w-full" />
          </div>
        </div>
      </div>

      <div class="p-0">
        <template v-if="loadingCampuses && !campuses.length && !debouncedSearch.trim()">
          <div class="flex items-center justify-center py-10">
            <span class="pi pi-spin pi-spinner text-2xl text-primary"></span>
          </div>
        </template>
        <template v-else>
          <DataTable :value="campuses" :paginator="campuses.length > 10" :rows="10"
            :loading="loadingCampuses" dataKey="id" size="small" stripedRows>
            <template #empty>
              <div class="py-6 text-center text-gray-400 text-xs">No campuses found</div>
            </template>
            <Column field="name" header="Campus Name" :sortable="true" />
            <Column header="Students" style="width: 8rem" :sortable="true" sortField="student_count">
              <template #body="{ data }">
                <span class="bg-primary/10 text-primary-700 px-2 py-0.5 rounded text-xs font-bold">
                  {{ data.student_count ?? 0 }}
                </span>
              </template>
            </Column>
            <Column header="Actions" style="width: 7rem">
              <template #body="{ data }">
                <div class="flex gap-1">
                  <Button icon="pi pi-users" size="small" text rounded severity="info"
                    @click="openAssign(data)" v-tooltip="'Assign Students'" />
                  <Button icon="pi pi-pencil" size="small" text rounded severity="secondary"
                    @click="openEdit(data)" v-tooltip="'Edit'" />
                  <Button icon="pi pi-trash" size="small" text rounded severity="danger"
                    @click="handleDelete(data)" v-tooltip="'Delete'" data-testid="btn-settings-campus-delete" />
                </div>
              </template>
            </Column>
          </DataTable>
        </template>
      </div>
    </div>

    <!-- Edit Dialog -->
    <Dialog v-model:visible="showEditDialog" header="Edit Campus" modal :style="{ width: '420px' }">
      <div class="flex flex-col gap-2 pt-1">
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-600">Campus Name *</label>
          <InputText v-model="editName" placeholder="Campus name" class="w-full" />
        </div>
      </div>
      <div class="flex justify-end gap-1.5 mt-4">
        <Button label="Cancel" size="small" severity="secondary" text @click="showEditDialog = false" />
        <Button label="Save" size="small" icon="pi pi-check" :loading="updating" :disabled="!editName.trim()"
          @click="executeUpdate" />
      </div>
    </Dialog>

    <!-- Assign Students Dialog -->
    <Dialog v-model:visible="showAssignDialog" :header="'Assign Students to ' + (assignCampus?.name ?? 'Campus')" modal :style="{ width: '500px' }">
      <div class="flex flex-col gap-3 pt-2">
        <p class="text-sm text-gray-600">Search and select students to assign to <strong>{{ assignCampus?.name }}</strong>.</p>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Students</label>
          <MultiSelect v-model="selectedStudentIds" :options="studentOptions" optionLabel="label" optionValue="value"
            placeholder="Search by matric or name..." filter @filter="onStudentFilter" :maxSelectedLabels="5" class="w-full" />
          <small class="text-gray-500">{{ selectedStudentIds.length }} student(s) selected</small>
        </div>
      </div>
      <div class="flex justify-end gap-1.5 mt-4">
        <Button label="Cancel" size="small" severity="secondary" text @click="showAssignDialog = false" />
        <Button label="Assign" size="small" icon="pi pi-check" :loading="assigning"
          :disabled="selectedStudentIds.length === 0" @click="executeAssign" />
      </div>
    </Dialog>

    <!-- Delete Dialog -->
    <Dialog v-model:visible="showDeleteDialog" header="Delete Campus" modal :style="{ width: '420px' }">
      <div class="flex items-start gap-2 pt-1.5">
        <div class="flex items-center justify-center w-8 h-8 rounded-full bg-red-50 shrink-0">
          <i class="pi pi-trash text-red-500 text-sm"></i>
        </div>
        <div>
          <p class="text-sm font-medium text-gray-800">Are you sure?</p>
          <p class="text-xs text-gray-500 mt-0.5">
            This will permanently delete
            <strong v-if="campusToDelete">"{{ campusToDelete.name }}"</strong>.
          </p>
          <p v-if="(campusToDelete?.student_count ?? 0) > 0" class="text-xs text-red-600 mt-1 font-medium">
            <i class="pi pi-exclamation-triangle mr-1"></i>
            This campus has {{ campusToDelete?.student_count }} student(s) and cannot be deleted. Reassign them first.
          </p>
          <p v-else class="text-xs text-orange-600 mt-1">
            <i class="pi pi-info-circle mr-1"></i>
            This action cannot be undone.
          </p>
        </div>
      </div>
      <div class="flex justify-end gap-1.5 mt-4">
        <Button label="Cancel" size="small" severity="secondary" text @click="showDeleteDialog = false" />
        <Button label="Delete" size="small" severity="danger" icon="pi pi-trash" :loading="deleting"
          :disabled="(campusToDelete?.student_count ?? 0) > 0" @click="executeDelete" />
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { gql } from '@apollo/client/core';
import { useToast } from 'primevue/usetoast';
import { useMutation } from '@vue/apollo-composable';
import { refDebounced } from '@vueuse/core';
import { useCampuses } from '@/composables/queries/useCampuses';
import { useStudents } from '@/composables/queries/useStudent';
import { useFormErrors } from '@/composables/useFormErrors';
import { useCreateCampusMutation, useDeleteCampusMutation, useUpdateCampusMutation } from '@/types/vue-apollo-helpers';
import type { CampusType } from '@/types/composition-functions';
import { EnrollmentStatusEnum } from '@/types/composition-functions';

const toast = useToast();

const showCreateForm = ref(false);
const showDeleteDialog = ref(false);
const showEditDialog = ref(false);
const campusToDelete = ref<CampusType | null>(null);
const campusToEdit = ref<CampusType | null>(null);
const deleting = ref(false);
const updating = ref(false);
const campusName = ref('');
const editName = ref('');
const searchQuery = ref('');
const debouncedSearch = refDebounced(searchQuery, 300);

const campusFilter = computed(() => debouncedSearch.value.trim() ? { query: debouncedSearch.value.trim() } : undefined);
const { campuses, loadingCampuses, refetchCampuses } = useCampuses({ autoFetch: true, filter: campusFilter });
const { mutate: createCampus, loading: creating } = useCreateCampusMutation();
const { mutate: deleteCampus } = useDeleteCampusMutation();
const { mutate: updateCampus } = useUpdateCampusMutation();

const ASSIGN_STUDENTS = gql`
  mutation AssignStudentsToCampus($campus_id: ID!, $student_ids: [ID!]!) {
    assignStudentsToCampus(campus_id: $campus_id, student_ids: $student_ids) { success message }
  }
`;
const { mutate: assignMutate, loading: assigning } = useMutation(ASSIGN_STUDENTS);

const showAssignDialog = ref(false);
const assignCampus = ref<CampusType | null>(null);
const selectedStudentIds = ref<string[]>([]);

const studentSearchFilters = ref({ first: 50, page: 1, filter: { search: '', status: EnrollmentStatusEnum.Enrolled } });
const { students: assignStudents, loadStudents: loadAssignStudents } = useStudents({
  getStudentsOptions: { autoFetch: false },
  studentFilters: studentSearchFilters as never,
});
let sDebounce: ReturnType<typeof setTimeout> | null = null;
function onStudentFilter(event: { value: string }) {
  if (sDebounce) clearTimeout(sDebounce);
  sDebounce = setTimeout(() => {
    studentSearchFilters.value = { first: 50, page: 1, filter: { search: event.value, status: EnrollmentStatusEnum.Enrolled } };
    loadAssignStudents();
  }, 300);
}
const studentOptions = computed(() =>
  assignStudents.data.value.map((s) => ({ label: `${s.matric_no} - ${s.surname} ${s.first_name}`, value: s.id })),
);

function openAssign(campus: CampusType) {
  assignCampus.value = campus;
  selectedStudentIds.value = [];
  showAssignDialog.value = true;
}

async function executeAssign() {
  if (!assignCampus.value || selectedStudentIds.value.length === 0) return;
  try {
    const res = await assignMutate({ campus_id: assignCampus.value.id, student_ids: selectedStudentIds.value });
    const data = res?.data?.assignStudentsToCampus;
    if (data?.success) {
      toast.add({ severity: 'success', summary: 'Assigned', detail: data.message, life: 3000 });
      showAssignDialog.value = false;
      assignCampus.value = null;
      selectedStudentIds.value = [];
      refetchCampuses();
    } else {
      toast.add({ severity: 'error', summary: 'Error', detail: data?.message, life: 5000 });
    }
  } catch (error: unknown) {
    toast.add({ severity: 'error', summary: 'Error', detail: (error as Error).message, life: 5000 });
  }
}
const { serverErrors, handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();

const createNewCampus = async() => {
  clearErrors();
  if (!campusName.value.trim()) {
    serverErrors.value.name = ['Campus name is required'];
    return;
  }
  try {
    await createCampus({ input: { name: campusName.value.trim() } });
    toast.add({ severity: 'success', summary: 'Success', detail: 'Campus created successfully', life: 3000 });
    campusName.value = '';
    refetchCampuses();
  } catch (error: unknown) {
    handleServerErrors(error);
    if (Object.keys(serverErrors.value).length === 0) {
      const message = error instanceof Error ? error.message : 'Failed to create campus';
      toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
    }
  }
};

function openEdit(campus: CampusType) {
  campusToEdit.value = campus;
  editName.value = campus.name;
  showEditDialog.value = true;
}

async function executeUpdate() {
  if (!campusToEdit.value || !editName.value.trim()) return;
  updating.value = true;
  try {
    await updateCampus({ id: campusToEdit.value.id, input: { name: editName.value.trim() } });
    toast.add({ severity: 'success', summary: 'Updated', detail: 'Campus updated successfully', life: 3000 });
    showEditDialog.value = false;
    campusToEdit.value = null;
    refetchCampuses();
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to update campus';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  } finally {
    updating.value = false;
  }
}

function handleDelete(campus: CampusType) {
  campusToDelete.value = campus;
  showDeleteDialog.value = true;
}

async function executeDelete() {
  if (!campusToDelete.value) return;
  deleting.value = true;
  try {
    await deleteCampus({ id: campusToDelete.value.id });
    toast.add({ severity: 'success', summary: 'Deleted', detail: 'Campus deleted successfully', life: 3000 });
    refetchCampuses();
    showDeleteDialog.value = false;
    campusToDelete.value = null;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to delete campus';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
  } finally {
    deleting.value = false;
  }
}
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.25s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
